import { useEffect, useRef, useState } from "react";
import { submitLead } from "@/lib/submit-lead.functions";
import { getTurnstileToken, preloadTurnstile } from "@/lib/turnstile";
import { captureTrackingParams, type TrackingParams } from "@/lib/tracking-params";
import { hashUserData, splitName } from "@/lib/hash-user-data";
import { pushLeadEvent } from "@/lib/lead-event";
import { isServiceZip } from "@/data/service-zips";
import { SeasonNotice } from "@/components/season-banner";

import {
  calculateInstantQuote,
  COUPON_AMOUNT,
  COUPON_CODE,
  HOME_SIZES,
  INSTANT_SERVICES,
  money,
  serviceShare,

  type HomeSizeId,
  type InstantServiceId,
  type QuoteBreakdown,
} from "@/data/instant-quote-pricing";
import {
  Check,
  Phone,
  ShieldCheck,
  Star,
  CalendarCheck,
  Tag,
  ChevronLeft,
} from "lucide-react";

import houseThumb from "@/assets/opt/service-house-washing.webp";
import roofThumb from "@/assets/service-heroes/service-roof-washing-custom.webp.asset.json";
import windowThumb from "@/assets/service-heroes/service-window-cleaning.webp.asset.json";
import gutterThumb from "@/assets/guide-fall-gutter-checklist.webp.asset.json";
import concreteThumb from "@/assets/opt/service-concrete-cleaning-sealing.webp";
import paverThumb from "@/assets/opt/service-paver-cleaning-sealing-clifton-park.webp";

const SERVICE_THUMBS: Record<InstantServiceId, string> = {
  house: houseThumb,
  roof: roofThumb.url,
  window: windowThumb.url,
  gutter: gutterThumb.url,
  concrete: concreteThumb,
  pavers: paverThumb,
};


const emailRx = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
function normalizePhone(v: string) {
  return v.replace(/\D/g, "");
}
function formatPhone(v: string) {
  const d = normalizePhone(v).slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 7) return `(${d.slice(0, 3)}) ${d.slice(3)}`;
  return `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`;
}
function isValidUsPhone(v: string) {
  const d = normalizePhone(v);
  return d.length === 10 && !/^[01]/.test(d);
}

function pushEvent(payload: Record<string, unknown>) {
  void pushLeadEvent(payload);
}


/** 24-hour offer countdown, persisted so a refresh doesn't reset it. */
const OFFER_KEY = "cp_iq_offer_exp";
function useOfferCountdown(active: boolean) {
  const [expiresAt, setExpiresAt] = useState<number | null>(null);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    // Warm the bot-check script so submit stays instant.
    preloadTurnstile();
  }, []);
  useEffect(() => {
    if (!active) return;
    let exp = 0;
    try {
      exp = parseInt(localStorage.getItem(OFFER_KEY) || "0", 10) || 0;
      if (!exp || exp < Date.now()) {
        exp = Date.now() + 24 * 60 * 60 * 1000;
        localStorage.setItem(OFFER_KEY, String(exp));
      }
    } catch {
      exp = Date.now() + 24 * 60 * 60 * 1000;
    }
    setExpiresAt(exp);
    const t = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(t);
  }, [active]);
  if (!expiresAt) return null;
  const ms = Math.max(0, expiresAt - now);
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  return `${h}h ${String(m).padStart(2, "0")}m ${String(s).padStart(2, "0")}s`;
}

export function InstantQuoteBuilder() {
  const [step, setStep] = useState(1);
  const [stepDir, setStepDir] = useState<1 | -1>(1);
  const [services, setServices] = useState<InstantServiceId[]>([]);
  const [size, setSize] = useState<HomeSizeId | null>(null);
  const [fullName, setFullName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [zip, setZip] = useState("");
  const [outOfArea, setOutOfArea] = useState(false);
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [submitted, setSubmitted] = useState(false);
  const submittingRef = useRef(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const [tracking, setTracking] = useState<TrackingParams | null>(null);
  const [previewMode, setPreviewMode] = useState(false);
  useEffect(() => {
    setTracking(captureTrackingParams());
    try {
      setPreviewMode(new URLSearchParams(window.location.search).get("preview") === "1");
    } catch {
      /* ignore */
    }
  }, []);

  const quote: QuoteBreakdown | null =
    size !== null && services.length > 0 ? calculateInstantQuote(services, size) : null;
  const countdown = useOfferCountdown(submitted && !!quote?.couponEligible);

  function go(next: number) {
    setStepDir(next > step ? 1 : -1);
    setStep(next);
    pushEvent({
      event: "instant_quote_step",
      step: next,
      services,
      home_size: size,
      page_path: window.location.pathname,
    });
    cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function toggleService(id: InstantServiceId) {
    setServices((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  }

  function stepValid(s: number): boolean {
    if (s === 1) return services.length > 0;
    if (s === 2) return size !== null;
    if (s === 4) {
      const emailOk = email.trim().length === 0 || emailRx.test(email.trim());
      return fullName.trim().length > 0 && isValidUsPhone(phone) && emailOk;
    }
    return true;
  }

  function handleContinue() {
    if (!stepValid(step)) {
      if (step === 4) setTouched({ fullName: true, phone: true, email: true });
      return;
    }
    if (previewMode && step === 2) {
      setSubmitted(true);
      cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      return;
    }
    if (step < 4) go(step + 1);
    else handleSubmit();
  }

  function handleSubmit() {
    if (submittingRef.current || !quote || !size) return;
    submittingRef.current = true;
    const sizeLabel = HOME_SIZES.find((s) => s.id === size)!.label;
    const eventId =
      typeof crypto !== "undefined" && "randomUUID" in crypto
        ? crypto.randomUUID()
        : `lead_${Date.now()}_${Math.random().toString(36).slice(2)}`;
    const message =
      `INSTANT QUOTE (website calculator). Services: ${INSTANT_SERVICES.filter((s) => services.includes(s.id))
        .map((s) => s.label)
        .join(", ")}. Home size: ${sizeLabel}. ` +
      `Quoted price: ${money(quote.finalPrice)} (typical range ${money(quote.rangeLow)}–${money(quote.rangeHigh)})` +
      (quote.couponEligible ? ` after $${COUPON_AMOUNT} new-customer discount` : "") +
      (quote.hasFromPricing ? ". Includes from-pricing lines (verify on site)" : "") +
      ".";
    void (async () => {
      // Save in the background (with the bot check inside that chain) so the
      // conversion event and the results screen never wait on it.
      void getTurnstileToken()
        .then((turnstileToken) =>
          submitLead({
          data: {
            turnstileToken,
            fullName: fullName.trim(),
            phone: normalizePhone(phone),
            email: email.trim(),
            address: address.trim(),
            city: city.trim(),
            state: "NY",
            zip: zip.trim(),
            propertyType: "Home",
            services,
            timeline: "Instant quote",
            message,
            bundleDiscount: services.length >= 2,
            serviceCount: services.length,
            plan: "",
            outOfArea,
            tracking: tracking ?? captureTrackingParams(),
            eventId,
          },
          }),
        )
        .catch((err) => {
          console.error("[instant-quote] submit error", err);
        });
      const { firstName, lastName } = splitName(fullName.trim());
      const hashed = await hashUserData({
        fullName: fullName.trim(),
        email: email.trim(),
        phone: normalizePhone(phone),
      });
      pushEvent({
        event: "lead_submitted",
        event_id: eventId,
          user_data: {
            email_address: email.trim(),
            phone_number: normalizePhone(phone),
            address: {
              first_name: firstName,
              last_name: lastName,
              street: address.trim(),
              city: city.trim(),
              region: "NY",
              postal_code: zip.trim(),
              country: "US",
            },
          },
          // SHA-256 hashed, Meta Advanced Matching format (em/ph/fn/ln).
          user_data_hashed: hashed,
        form: "instant_quote",
        services,
        home_size: size,
        quoted_price: quote.finalPrice,
        page_path: window.location.pathname,
      });
      try {
        localStorage.setItem("cp_lead_submitted", "1");
      } catch {
        /* ignore */
      }
      setSubmitted(true);
      cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    })();
  }

  const totalSteps = previewMode ? 2 : 4;
  const progressPct = submitted ? 100 : (step / totalSteps) * 100;

  return (
    <div ref={cardRef} className="iq-card" id="instant-quote">
      {!submitted && (
        <div className="qm-progress-wrap" aria-hidden="true">
          <div className="qm-progress-meta">
            <span>Step {step} of {totalSteps}</span>
          </div>
          <div className="qm-progress-track">
            <div className="qm-progress-fill" style={{ width: `${progressPct}%` }} />
          </div>
        </div>
      )}

      <div className="iq-body">
        {submitted && size ? (
          <ResultsView
            quote={quote}
            services={services}
            sizeId={size}
            onToggleService={toggleService}
            countdown={countdown}
            fullName={fullName.trim()}
            email={email.trim()}
            phone={normalizePhone(phone)}
            address={address.trim()}
            city={city.trim()}
            zip={zip.trim()}
            outOfArea={outOfArea}
          />
        ) : (
          <div key={step} className={`qm-step qm-step-${stepDir > 0 ? "in-right" : "in-left"}`}>
            {step === 1 && (
              <div>
                <SeasonNotice className="mb-4" />
                <h2 className="qm-title">What pressure washing services are you interested in?</h2>
                <p className="qm-sub">Select all that apply. Bundle and save a trip.</p>
                <div className="iq-srv-list" role="group" aria-label="Services">
                  {INSTANT_SERVICES.map((s) => {
                    const active = services.includes(s.id);
                    return (
                      <button
                        key={s.id}
                        type="button"
                        aria-pressed={active}
                        onClick={() => toggleService(s.id)}
                        className={`iq-srv-row ${active ? "iq-srv-row-active" : ""}`}
                      >
                        <img
                          src={SERVICE_THUMBS[s.id]}
                          alt=""
                          aria-hidden="true"
                          loading="lazy"
                          decoding="async"
                          className="iq-srv-thumb"
                        />
                        <span className="iq-srv-text">
                          <span className="iq-srv-label">{s.label}</span>
                          <span className="iq-srv-sub">{s.sub}</span>
                        </span>
                        <span className={`iq-srv-box ${active ? "iq-srv-box-on" : ""}`} aria-hidden="true">
                          {active && <Check className="h-3.5 w-3.5" />}
                        </span>
                      </button>
                    );
                  })}
                </div>
                <p className="iq-srv-count" role="status">
                  {services.length === 0
                    ? "Select at least one service"
                    : `${services.length} service${services.length > 1 ? "s" : ""} selected${
                        services.length >= 2 ? " — bundle savings applied" : ""
                      }`}
                </p>
              </div>
            )}

            {step === 2 && (
              <div>
                <h2 className="qm-title">How big is your home?</h2>
                <p className="qm-sub">Approximate square footage is fine.</p>
                <div className="iq-srv-list" role="group" aria-label="Home size">
                  {HOME_SIZES.map((s) => {
                    const active = size === s.id;
                    return (
                      <button
                        key={s.id}
                        type="button"
                        aria-pressed={active}
                        onClick={() => setSize(s.id)}
                        className={`iq-srv-row ${active ? "iq-srv-row-active" : ""}`}
                      >
                        <span className="iq-srv-text">
                          <span className="iq-srv-label">{s.label}</span>
                          <span className="iq-srv-sub">{s.sub}</span>
                        </span>
                        <span className={`iq-srv-box ${active ? "iq-srv-box-on" : ""}`} aria-hidden="true">
                          {active && <Check className="h-3.5 w-3.5" />}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}


            {step === 3 && (
              <div>
                <h2 className="qm-title">What's the service address?</h2>
                <p className="qm-sub">Optional — it just helps us price the job faster.</p>
                <div className="qm-field">
                  <label htmlFor="iq-address" className="qm-label">
                    Street address <span className="font-normal text-[var(--brand-charcoal)]/50">(optional)</span>
                  </label>
                  <input
                    id="iq-address"
                    type="text"
                    autoComplete="street-address"
                    className="qm-input"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    placeholder="123 Main St"
                  />
                </div>
                <div className="qm-field">
                  <label htmlFor="iq-city" className="qm-label">
                    City <span className="font-normal text-[var(--brand-charcoal)]/50">(optional)</span>
                  </label>
                  <input
                    id="iq-city"
                    type="text"
                    autoComplete="address-level2"
                    className="qm-input"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    placeholder="Albany"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="qm-field">
                    <label htmlFor="iq-state" className="qm-label">
                      State
                    </label>
                    <input
                      id="iq-state"
                      type="text"
                      autoComplete="address-level1"
                      className="qm-input"
                      value="NY"
                      readOnly
                    />
                  </div>
                  <div className="qm-field">
                    <label htmlFor="iq-zip" className="qm-label">
                      ZIP code <span className="font-normal text-[var(--brand-charcoal)]/50">(optional)</span>
                    </label>
                    <input
                      id="iq-zip"
                      type="text"
                      autoComplete="postal-code"
                      inputMode="numeric"
                      maxLength={5}
                      className="qm-input"
                      value={zip}
                      onChange={(e) => {
                        const z = e.target.value.replace(/\D/g, "").slice(0, 5);
                        setZip(z);
                        setOutOfArea(z.length >= 5 && !isServiceZip(z));
                      }}
                      placeholder="12203"
                    />
                  </div>
                </div>
                {outOfArea && (
                  <p className="mt-2 rounded-md border border-[var(--brand-red)]/30 bg-[var(--brand-red)]/5 px-3 py-2 text-sm text-[var(--brand-red)]">
                    That ZIP looks outside our Capital Region service area — send the form anyway and we'll confirm, or
                    call <a href="tel:+15189001913" className="font-semibold underline">(518) 900-1913</a>.
                  </p>
                )}
              </div>
            )}

            {step === 4 && (
              <div>
                <h2 className="qm-title">Where do we send your price?</h2>
                <p className="qm-sub">Your instant quote shows on the next screen.</p>
                <div className="qm-field">
                  <label htmlFor="iq-name" className="qm-label">
                    Full name
                  </label>
                  <input
                    id="iq-name"
                    type="text"
                    autoComplete="name"
                    className={`qm-input ${touched.fullName && !fullName.trim() ? "qm-input-err" : ""}`}
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, fullName: true }))}
                    required
                  />
                </div>
                <div className="qm-field">
                  <label htmlFor="iq-phone" className="qm-label">
                    Phone
                  </label>
                  <input
                    id="iq-phone"
                    type="tel"
                    autoComplete="tel"
                    inputMode="tel"
                    className={`qm-input ${touched.phone && !isValidUsPhone(phone) ? "qm-input-err" : ""}`}
                    value={phone}
                    onChange={(e) => setPhone(formatPhone(e.target.value))}
                    onBlur={() => setTouched((t) => ({ ...t, phone: true }))}
                    placeholder="(518) 555-0123"
                    required
                  />
                  {touched.phone && !isValidUsPhone(phone) && (
                    <p className="qm-err">Enter a valid 10-digit US phone number.</p>
                  )}
                </div>
                <div className="qm-field">
                  <label htmlFor="iq-email" className="qm-label">
                    Email <span className="font-normal text-[var(--brand-charcoal)]/50">(optional)</span>
                  </label>
                  <input
                    id="iq-email"
                    type="email"
                    autoComplete="email"
                    inputMode="email"
                    className={`qm-input ${touched.email && email.trim() && !emailRx.test(email.trim()) ? "qm-input-err" : ""}`}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => setTouched((t) => ({ ...t, email: true }))}
                    placeholder="you@example.com"
                  />
                </div>
              </div>
            )}

          </div>
        )}
      </div>

      {!submitted && (
        <div className="qm-footer">
          <div className="qm-nav">
            {step > 1 ? (
              <button type="button" className="qm-btn-secondary" onClick={() => go(step - 1)}>
                <ChevronLeft className="h-4 w-4" aria-hidden="true" /> Back
              </button>
            ) : (
              <span />
            )}
            <button type="button" className="qm-btn-primary" onClick={handleContinue} disabled={!stepValid(step)}>
              {step === totalSteps ? "Show My Instant Price" : "Continue"}
            </button>
          </div>
          {step === 4 && (
            <p className="qm-consent">
              By submitting, you agree to be contacted by phone or text about your quote. Msg/data rates may apply.
            </p>
          )}
          <p className="qm-trust">No obligation. No spam. Local crew, 10+ years.</p>
        </div>
      )}
    </div>
  );
}

function ResultsView({
  quote,
  services,
  sizeId,
  onToggleService,
  countdown,
  fullName,
  email,
  phone,
  address,
  city,
  zip,
  outOfArea,
}: {
  quote: QuoteBreakdown | null;
  services: InstantServiceId[];
  sizeId: HomeSizeId;
  onToggleService: (id: InstantServiceId) => void;
  countdown: string | null;
  fullName: string;
  email: string;
  phone: string;
  address: string;
  city: string;
  zip: string;
  outOfArea: boolean;
}) {
  const sizeLabel = HOME_SIZES.find((s) => s.id === sizeId)!.label;
  const e164 = phone.length === 10 ? `+1${phone}` : `+${phone}`;
  const bookParams = new URLSearchParams();
  const nameParts = fullName.trim().split(/\s+/);
  if (nameParts[0]) bookParams.set("first_name", nameParts[0]);
  if (nameParts.length > 1) bookParams.set("last_name", nameParts.slice(1).join(" "));
  if (email) bookParams.set("email", email);
  if (phone) bookParams.set("phone", e164);
  if (address) bookParams.set("address", address);
  if (city) bookParams.set("city", city);
  if (zip) bookParams.set("zip", zip);

  return (
    <div className="iq-results">
      <p className="iq-results-kicker">
        <Check className="h-4 w-4" aria-hidden="true" /> Your instant price
      </p>
      <p className="iq-results-context">
        {INSTANT_SERVICES.filter((s) => services.includes(s.id))
          .map((s) => s.label)
          .join(" + ")}{" "}
        · {sizeLabel}
      </p>



      <div className="iq-price-block">
        {!quote ? (
          <p className="iq-price-note">Select at least one service to see your price.</p>
        ) : (
          <>
            {quote.couponEligible ? (
              <>
                <p className="iq-price-was">{money(quote.finalPrice + COUPON_AMOUNT)}</p>
                <p className="iq-price">{money(quote.finalPrice)}</p>
                <p className="iq-price-note">with your ${COUPON_AMOUNT} new-customer discount</p>
              </>
            ) : (
              <p className="iq-price">
                {quote.hasFromPricing && quote.lines.length === 1 ? "from " : ""}
                {money(quote.finalPrice)}
              </p>
            )}
            <p className="iq-range">
              Typical range for your home: {money(quote.rangeLow)} – {money(quote.rangeHigh)}
            </p>
          </>
        )}
      </div>

      {outOfArea && (
        <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">
          <strong>Outside our normal service area.</strong> We may still be able to help — or refer you to a trusted
          partner nearby. We’ll confirm after you book a call.
        </div>
      )}

      {quote?.couponEligible && (
        <div className="iq-coupon">
          <div className="iq-coupon-top">
            <Tag className="h-5 w-5 shrink-0" aria-hidden="true" />
            <div>
              <p className="iq-coupon-title">${COUPON_AMOUNT} off — applied automatically</p>
              <p className="iq-coupon-sub">
                {quote.couponSmallHome
                  ? "Good on homes up to 2,500 sq ft when you book this month."
                  : "Applied when you book this month."}
              </p>
            </div>
          </div>
          <div className="iq-coupon-code-row">
            <span className="iq-coupon-code">{COUPON_CODE}</span>
            {countdown && (
              <span className="iq-coupon-timer" role="timer">
                Holds your price for {countdown}
              </span>
            )}
          </div>
        </div>
      )}

      <div className="iq-lines">
        <p className="iq-lines-title">Tap to add or remove a service — price updates instantly:</p>
        {INSTANT_SERVICES.map((s) => {
          const active = services.includes(s.id);
          return (
            <button
              key={s.id}
              type="button"
              aria-pressed={active}
              onClick={() => onToggleService(s.id)}
              className={`iq-line ${active ? "iq-line-active" : ""}`}
            >
              <span className="flex items-center gap-2 min-w-0">
                <span className={`iq-line-dot ${active ? "iq-line-dot-on" : ""}`} aria-hidden="true">
                  {active && <Check className="h-3 w-3" />}
                </span>
                <span className="truncate font-semibold">{s.label}</span>
              </span>
              <span className="text-sm font-bold whitespace-nowrap">
                {(() => {
                  if (!sizeId) return active ? "—" : "add";
                  const current = quote?.finalPrice ?? 0;
                  if (active) {
                    const share = serviceShare(services, sizeId, s.id);
                    return `${s.fromOnly ? "from " : ""}${money(share)}`;
                  }
                  const withIt = calculateInstantQuote([...services, s.id], sizeId);
                  const delta = Math.max((withIt?.finalPrice ?? 0) - current, 0);
                  return `add ${s.fromOnly ? "from " : ""}${money(delta)}`;
                })()}

              </span>
            </button>
          );
        })}
      </div>

      <p className="iq-honesty">
        This price is based on the details you gave us. We verify roof pitch, driveway size, and access before
        scheduling, and confirm your final price with you — no surprises on service day.
      </p>

      <SeasonNotice className="mb-4" />
      <div className="iq-actions">
        {quote ? (
          <a href={`/thank-you?${bookParams.toString()}`} className="qm-btn-primary iq-action-book">
            <CalendarCheck className="h-4 w-4" aria-hidden="true" /> Schedule a Quick Call
          </a>
        ) : (
          <button type="button" className="qm-btn-primary iq-action-book" disabled>
            <CalendarCheck className="h-4 w-4" aria-hidden="true" /> Schedule a Quick Call
          </button>
        )}
        <a href="tel:+15189001913" className="qm-btn-call iq-action-call">
          <Phone className="h-4 w-4" aria-hidden="true" /> Questions? Call (518) 900-1913
        </a>
      </div>

      <p className="iq-guarantee">
        <ShieldCheck className="h-4 w-4" aria-hidden="true" /> 100% satisfaction guarantee — we re-clean anything
        you&apos;re not happy with, free.
      </p>
      <p className="iq-reviews">
        <Star className="h-4 w-4 fill-current" aria-hidden="true" /> 5.0 on Google from Capital Region homeowners
      </p>
      {quote && (
        <p className="text-center">
          <a href={`/thank-you?${bookParams.toString()}`} className="iq-text-link">
            Not ready? Save my quote for later
          </a>
        </p>
      )}
    </div>
  );
}
